import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowLeft, AlertCircle, Loader2, Store, Clock } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import authBackground from "@/assets/auth-background.jpg";
import ContactInfo from "@/components/ContactInfo";

function isValidBrazilContact(contact: string): boolean {
  const digits = contact.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 11;
}

function isValidSenha(senha: string): boolean {
  return senha.length === 4;
}

interface Agendamento {
  id: string;
  NOME: string;
  CONTATO: string;
  DATA: string;
  HORA: string;
  PROFISSIONAL: string;
  servico: string;
  STATUS: string;
  senha: string;
}

interface LojaConfig {
  opening_time?: string;
  closing_time?: string;
  slot_interval_minutes?: number;
  nome_profissionais?: string;
  escolha_serviços?: string;
}

interface SpecialInfo {
  isClosed?: boolean;
  closedMessage?: string;
  isSpecialHours?: boolean;
  specialHoursMessage?: string;
  specialHoursOpening?: string;
  specialHoursClosing?: string;
  isHoliday?: boolean;
  holiday?: { descricao: string };
}

export default function Reschedule() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<LojaConfig | null>(null);
  const [oldContact, setOldContact] = useState("");
  const [senha, setSenha] = useState("");
  const [professional, setProfessional] = useState<string>("");
  const [selectedDateCard, setSelectedDateCard] = useState<string | null>(null);
  const [slotsForSelectedDate, setSlotsForSelectedDate] = useState<string[]>([]);
  const [specialInfo, setSpecialInfo] = useState<SpecialInfo | null>(null);
  const [specialDatesList, setSpecialDatesList] = useState<any[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [pros, setPros] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [service, setService] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [userBookings, setUserBookings] = useState<Agendamento[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Agendamento | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canReschedule = useMemo(() => {
    return isValidBrazilContact(oldContact) && isValidSenha(senha) && selectedBooking !== null && selectedSlot !== null && selectedDateCard !== null && professional && service;
  }, [oldContact, senha, selectedBooking, selectedSlot, selectedDateCard, professional, service]);

  const brazilNow = useMemo(() => {
    const now = new Date();
    const date = now.toLocaleString("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit" }).split('T')[0];
    const time = now.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
    return { date, time };
  }, []);

  const isClosingTimePassed = useMemo(() => {
    if (!config?.closing_time) return false;
    const [cH, cM] = brazilNow.time.split(':').map(Number);
    const [kH, kM] = config.closing_time.slice(0, 5).split(':').map(Number);
    return (cH * 60 + cM) >= (kH * 60 + kM);
  }, [config?.closing_time, brazilNow.time]);

  useEffect(() => {
    document.title = "Reagendar atendimento | ÁSPERUS";
    (async () => {
      const { data } = await supabase.from("info_loja_public").select("*").limit(1).maybeSingle();
      if (data) setConfig(data);
    })();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const { data } = await supabase.from("info_loja_public").select("nome_profissionais, escolha_serviços");
        if (data) {
          const allP = data.map(r => r.nome_profissionais || "").join(";").split(/[;,\n]/).map(s => s.trim()).filter(Boolean);
          const allS = data.map(r => r.escolha_serviços || "").join(";").split(/[;,\n]/).map(s => s.trim()).filter(Boolean);
          setPros([...new Set(allP)]);
          setServices([...new Set(allS)]);
        }
      } catch (err) { console.error(err); }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (oldContact.length >= 8 && senha.length === 4) {
      (async () => {
        setLoadingBookings(true);
        try {
          const { data } = await supabase.functions.invoke('query-bookings', { body: { contact: oldContact, senha } });
          setUserBookings(data?.bookings || []);
        } catch (err) { console.error(err); setUserBookings([]); } finally { setLoadingBookings(false); }
      })();
    } else { setUserBookings([]); }
  }, [oldContact, senha]);

  const nextSixDates = useMemo(() => {
    const arr: string[] = [];
    const [y, m, d] = brazilNow.date.split('-').map(Number);
    const today = new Date(y, m - 1, d);
    let i = 0;
    while (arr.length < 6) {
      const target = new Date(today); target.setDate(today.getDate() + i);
      if (target.getDay() !== 0) arr.push(`${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, "0")}-${String(target.getDate()).padStart(2, "0")}`);
      i++;
    }
    return arr;
  }, [brazilNow.date]);

  const fetchSpecialDates = useCallback(async () => {
    try {
      const { data } = await supabase.from('horarios_especiais').select('*').gte('data', nextSixDates[0]).lte('data', nextSixDates[nextSixDates.length - 1]);
      if (data) setSpecialDatesList(data);
    } catch (err) { console.error(err); }
  }, [nextSixDates]);

  useEffect(() => { if (nextSixDates.length > 0) fetchSpecialDates(); }, [nextSixDates, fetchSpecialDates]);

  const fetchSlotsForDate = useCallback(async (dStr: string) => {
    if (!config?.opening_time || !config?.closing_time || !config?.slot_interval_minutes || !professional || !service) return;
    setLoadingSlots(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-available-slots", {
        body: { date: dStr, professional, opening_time: config.opening_time, closing_time: config.closing_time, slot_interval_minutes: config.slot_interval_minutes }
      });
      if (error) throw error;
      setSlotsForSelectedDate(data?.slots || []);
      setSpecialInfo({
        isClosed: data?.isClosed || false, closedMessage: data?.closedMessage || null, isSpecialHours: data?.isSpecialHours || false, specialHoursMessage: data?.specialHoursMessage || null,
        specialHoursOpening: data?.specialHoursOpening || null, specialHoursClosing: data?.specialHoursClosing || null, isHoliday: data?.isHoliday || false, holiday: data?.holiday || null
      });
    } catch (err) { console.error(err); toast.error("Erro ao buscar horários."); } finally { setLoadingSlots(false); }
  }, [config, professional, service]);

  const handleDateCardClick = useCallback((dateStr: string) => {
    setSelectedSlot(null);
    setSelectedDateCard(dateStr);
    const special = specialDatesList.find(s => s.data === dateStr);
    if (special) {
      setSpecialInfo({
        isClosed: special.tipo === "fechado", closedMessage: special.mensagem, isSpecialHours: special.tipo === "horario_especial", specialHoursMessage: special.mensagem,
        specialHoursOpening: special.horario_abertura, specialHoursClosing: special.horario_fechamento, isHoliday: false, holiday: null
      });
      if (special.tipo === "fechado") return;
    } else { setSpecialInfo(null); }
    if (professional && service) fetchSlotsForDate(dateStr);
  }, [fetchSlotsForDate, professional, service, specialDatesList]);

  useEffect(() => {
    if (nextSixDates.length > 0 && !selectedDateCard && config && specialDatesList.length >= 0) {
      handleDateCardClick(nextSixDates[0]);
    }
  }, [nextSixDates, config, selectedDateCard, specialDatesList, handleDateCardClick]);

  useEffect(() => { if (selectedDateCard && professional && service && config) { setSelectedSlot(null); fetchSlotsForDate(selectedDateCard); } }, [professional, service]);

  const specialInfoOverlay = useMemo(() => {
    if (!specialInfo) return null;
    if (specialInfo.isClosed) return { title: "Loja Fechada", message: specialInfo.closedMessage || "Estamos fechados nesta data.", type: "closed" as const, icon: <Store className="w-20 h-20 text-red-500 relative z-10" /> };
    if (specialInfo.isSpecialHours) {
      const hoursMsg = (specialInfo.specialHoursOpening && specialInfo.specialHoursClosing) 
        ? `Horário Especial: das ${specialInfo.specialHoursOpening.slice(0, 5)} às ${specialInfo.specialHoursClosing.slice(0, 5)}`
        : "";
      return { 
        title: "Horário Especial", 
        message: specialInfo.specialHoursMessage || "Funcionaremos em horário diferenciado.", 
        hours: hoursMsg,
        type: "special" as const, 
        icon: <Clock className="w-20 h-20 text-warning relative z-10" /> 
      };
    }
    return null;
  }, [specialInfo]);

  const handleSlotSelect = useCallback((slot: string) => {
    if (!slotsForSelectedDate.includes(slot)) { toast.error("Este horário não está mais disponível."); return; }
    setSelectedSlot(slot);
  }, [slotsForSelectedDate]);

  async function handleReschedule() {
    if (!canReschedule || !selectedBooking) return;
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("reschedule-booking", {
        body: { oldName: selectedBooking.NOME, oldContact: selectedBooking.CONTATO, oldDate: selectedBooking.DATA, oldTime: selectedBooking.HORA, newDate: selectedDateCard, newTime: selectedSlot, professional, service, senha: selectedBooking.senha }
      });
      if (error) throw error;
      navigate("/reschedule-confirmation", { state: { oldDate: selectedBooking.DATA, oldTime: selectedBooking.HORA, newDate: selectedDateCard, newTime: selectedSlot, contact: selectedBooking.CONTATO, professional, service, senha: data?.booking?.senha } });
    } catch (e: any) {
      console.error(e);
      toast.error("Erro ao reagendar.");
    } finally { setIsSubmitting(false); }
  }

  return (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat relative" style={{ backgroundImage: `url(${authBackground})` }}>
      <div className="absolute inset-0 bg-black/50"></div>
      <main className="container mx-auto px-6 py-8 relative z-10">
        <AnimatePresence>
          {specialInfoOverlay && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center px-4 overflow-hidden">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={cn("absolute inset-0 backdrop-blur-3xl", specialInfoOverlay.type === "closed" ? "bg-red-500/20" : "bg-black/80")} onClick={() => specialInfoOverlay.type === "special" && setSpecialInfo(null)} />
              <motion.div initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }} className={cn("relative w-full max-w-2xl p-12 rounded-[3.5rem] border-4 shadow-[0_50px_100px_rgba(0,0,0,0.8)] text-center overflow-hidden", specialInfoOverlay.type === "closed" ? "bg-zinc-900 border-red-500/30" : "bg-zinc-900/90 border-white/10")}>
                <div className={cn("absolute inset-0 opacity-5", specialInfoOverlay.type === "closed" ? "bg-red-500" : "bg-warning")} />
                <div className="relative z-10 flex flex-col items-center gap-8">
                  <div className={cn("p-10 rounded-full border-4 shadow-2xl", specialInfoOverlay.type === "closed" ? "bg-red-500/20 border-red-500/40 text-red-500" : "bg-warning/20 border-warning/40 text-warning")}>{specialInfoOverlay.icon}</div>
                  <div className="space-y-4">
                    <h2 className={cn("text-6xl sm:text-7xl font-black uppercase tracking-tighter leading-none decoration-orange-500/50 underline-offset-8 underline", specialInfoOverlay.type === "closed" ? "text-white" : "text-warning")}>{specialInfoOverlay.title}</h2>
                    <div className="space-y-3">
                      <p className="text-2xl sm:text-3xl text-slate-100 font-bold leading-tight">{specialInfoOverlay.message}</p>
                      {specialInfoOverlay.hours && (
                        <div className="inline-block px-6 py-2 bg-warning/20 border border-warning/30 rounded-full">
                          <p className="text-xl text-warning font-black uppercase tracking-widest">{specialInfoOverlay.hours}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-6 justify-center w-full">
                    {specialInfoOverlay.type === "closed" ? (
                      <div className="flex flex-col gap-4 w-full">
                        <Button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); navigate("/"); }} variant="outline" className="btn-skeuo-red w-full h-20 text-2xl font-black uppercase rounded-2xl shadow-[0_8px_0_#000]">VOLTAR AO INÍCIO</Button>
                      </div>
                    ) : (
                      <Button onClick={() => setSpecialInfo(null)} className="btn-skeuo-orange w-full h-22 text-white font-black rounded-2xl text-2xl uppercase tracking-tighter">CONTINUAR</Button>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={cn("transition-all duration-700 pb-20", specialInfoOverlay && "opacity-10 grayscale pointer-events-none blur-md shrink-0")}>
          <header className="mb-12 text-center relative">
            <Button variant="outline" className="md:absolute md:top-0 md:left-0 flex items-center gap-2 mb-6" onClick={() => navigate("/")}><ArrowLeft className="h-4 w-4" />Voltar</Button>
            <h1 className="text-5xl font-black text-primary uppercase tracking-tighter decoration-orange-500/50 underline-offset-8 underline">REAGENDAR</h1>
          </header>

          <Card className="mb-8 bg-black/40 backdrop-blur-xl border-white/5 shadow-2xl">
            <CardHeader>
              <Alert className="mb-4 border-primary/30 bg-primary/10"><AlertCircle className="h-4 w-4 text-primary" /><AlertDescription className="text-slate-300 font-bold uppercase">Agendamentos passados não podem ser alterados.</AlertDescription></Alert>
              <CardTitle className="text-primary uppercase font-black tracking-widest text-sm">Buscar Agendamento</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2"><Label className="text-slate-500 font-black uppercase tracking-widest text-xs">CONTATO</Label><Input value={oldContact} onChange={e => setOldContact(e.target.value)} placeholder="(21) 99876-5432" className="bg-zinc-900 border-0 h-16 rounded-2xl text-xl font-bold" /></div>
              <div className="space-y-2"><Label className="text-slate-500 font-black uppercase tracking-widest text-xs">SENHA (4 DÍGITOS)</Label><Input type="text" maxLength={4} value={senha} onChange={e => setSenha(e.target.value)} placeholder="0000" className="bg-zinc-900 border-0 h-16 rounded-2xl text-xl font-bold text-center" /></div>
            </CardContent>
          </Card>

          {userBookings.length > 0 && (
            <Card className="mb-8 bg-black/40 backdrop-blur-xl border-white/5">
              <CardHeader>
                <CardTitle className="text-primary font-black uppercase tracking-widest">Seus Atendimentos</CardTitle>
                <p className="text-sm text-slate-400 font-medium">
                  Selecione o agendamento que deseja reagendar
                </p>
              </CardHeader>
              <CardContent className="grid gap-4">
                {userBookings.map((b) => (
                  <div key={b.id} onClick={() => setSelectedBooking(b)} className={cn("p-8 border-4 rounded-[2.5rem] cursor-pointer transition-all flex flex-col md:flex-row justify-between items-center gap-6", selectedBooking?.id === b.id ? "border-primary bg-primary/10 shadow-[0_0_50px_rgba(245,158,11,0.2)]" : "border-white/5 bg-zinc-900/50 hover:border-white/20")}>
                    <div className="text-center md:text-left">
                      <p className="text-3xl font-black text-white">{b.PROFISSIONAL} | {b.servico}</p>
                      <p className="text-2xl font-bold text-primary tracking-tighter">{format(new Date(b.DATA + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR })} às {b.HORA.slice(0, 5)}</p>
                    </div>
                    {selectedBooking?.id === b.id && <div className="bg-primary text-black px-6 py-2 rounded-2xl font-black text-sm uppercase tracking-widest">SELECIONADO</div>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card className="mb-8 bg-black/40 backdrop-blur-xl border-white/5">
            <CardHeader><CardTitle className="text-primary font-black uppercase tracking-widest">Escolha a Data</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                {nextSixDates.map(d => {
                  const dateObj = new Date(d + 'T12:00:00');
                  const isSelected = selectedDateCard === d;
                  return (
                    <Card key={d} className={cn("cursor-pointer transition-all border-0 rounded-2xl bg-zinc-900 shadow-[0_6px_0_#000] active:translate-y-1 active:shadow-none", isSelected && "ring-4 ring-primary bg-primary/5")} onClick={() => handleDateCardClick(d)}>
                      <CardContent className="p-4 text-center">
                        <p className="text-[10px] text-slate-500 font-bold uppercase">{format(dateObj, "EEE", { locale: ptBR })}</p>
                        <p className={cn("text-3xl font-black", isSelected ? "text-primary" : "text-white")}>{format(dateObj, "dd", { locale: ptBR })}</p>
                        <p className="text-[10px] text-slate-500 font-bold uppercase">{format(dateObj, "MMM", { locale: ptBR })}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-8 md:grid-cols-2">
            <Card className={cn("bg-black/30 border-white/5 rounded-3xl", professional && "ring-2 ring-primary")}>
              <CardHeader><CardTitle className="uppercase font-black text-xs tracking-widest text-slate-500">Novo Profissional</CardTitle></CardHeader>
              <CardContent>
                <Select value={professional || undefined} onValueChange={setProfessional}>
                  <SelectTrigger className="bg-zinc-900 border-0 h-16 rounded-2xl text-xl font-bold"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                  <SelectContent>{pros.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </CardContent>
            </Card>
            <Card className={cn("bg-black/30 border-white/5 rounded-3xl", service && "ring-2 ring-primary")}>
              <CardHeader><CardTitle className="uppercase font-black text-xs tracking-widest text-slate-500">Novo Serviço</CardTitle></CardHeader>
              <CardContent>
                <Select value={service || undefined} onValueChange={setService}>
                  <SelectTrigger className="bg-zinc-900 border-0 h-16 rounded-2xl text-xl font-bold"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                  <SelectContent>{services.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-12 bg-black/40 border-white/5 rounded-[3rem]">
            <CardHeader className="text-center pt-8"><CardTitle className="text-4xl font-black uppercase text-primary tracking-tighter">Agenda Disponível</CardTitle></CardHeader>
            <CardContent className="p-10">
              {!selectedDateCard || !professional || !service ? (
                <div className="py-20 text-center opacity-30"><Clock className="w-16 h-16 mx-auto mb-4" /><p className="text-xl font-black uppercase tracking-widest">Complete as seleções acima</p></div>
              ) : loadingSlots ? (
                <div className="py-20 flex flex-col items-center gap-6"><Loader2 className="h-16 w-16 animate-spin text-primary" /><p className="text-xl font-bold uppercase tracking-widest text-slate-400">Consultando novos horários...</p></div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                  {slotsForSelectedDate.map(s => (
                    <button key={s} onClick={() => handleSlotSelect(s)} className={cn("h-16 rounded-2xl font-black text-xl transition-all border-b-8 active:translate-y-2 active:border-b-0", selectedSlot === s ? "bg-primary text-black border-orange-700 shadow-[0_15px_30px_rgba(245,158,11,0.4)]" : "bg-zinc-900 text-slate-500 border-black hover:border-primary/50 hover:text-white")}>{s}</button>
                  ))}
                  {slotsForSelectedDate.length === 0 && <p className="col-span-full py-12 text-center text-slate-500 font-black uppercase text-xl">Nenhum horário disponível para esta data</p>}
                </div>
              )}
            </CardContent>
          </Card>

          <Button onClick={handleReschedule} disabled={!canReschedule || isSubmitting} className={cn("w-full mt-12 h-28 text-4xl font-black transition-all rounded-[2.5rem] uppercase tracking-tighter shadow-2xl", canReschedule && !isSubmitting ? "btn-skeuo-orange text-white hover:scale-[1.02] active:scale-95 [text-shadow:0_3px_5px_rgba(0,0,0,0.8),0_0_15px_rgba(255,255,255,0.4)]" : "bg-zinc-800 text-zinc-600 grayscale")}>
            {isSubmitting ? <><Loader2 className="mr-6 h-12 w-12 animate-spin" />REPROCESSANDO...</> : "REAGENDAR"}
          </Button>
        </div>
        <div className="mt-20"><ContactInfo /></div>
      </main>
    </div>
  );
}
